/** Thrown for any non-2xx response; always carries the parsed problem document. */
export class ApiError extends Error {
    problem;
    response;
    constructor(problem, response) {
        super(problem.detail ?? problem.title);
        this.problem = problem;
        this.response = response;
        this.name = 'ApiError';
    }
    get status() {
        return this.problem.status;
    }
    get code() {
        return this.problem.code;
    }
}
export class HttpClient {
    options;
    baseUrl;
    fetchImpl;
    constructor(options) {
        this.options = options;
        this.baseUrl = options.baseUrl.replace(/\/$/, '');
        this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    }
    get(path, options) {
        return this.request('GET', path, options);
    }
    post(path, options) {
        return this.request('POST', path, options);
    }
    patch(path, options) {
        return this.request('PATCH', path, options);
    }
    put(path, options) {
        return this.request('PUT', path, options);
    }
    delete(path, options) {
        return this.request('DELETE', path, options);
    }
    async request(method, path, options = {}) {
        const url = this.buildUrl(path, options.query);
        const headers = new Headers({
            accept: 'application/json',
            ...this.options.defaultHeaders,
            ...options.headers,
        });
        const requestId = this.options.generateRequestId?.();
        if (requestId)
            headers.set('x-request-id', requestId);
        // A caller that sets `authorization` explicitly means it — ending a session
        // this app never adopted, for instance, where the store holds no token and
        // the only one that would work is the token just received. Such a request
        // is not using the session at all, which matters again at the 401 below.
        const callerSuppliedAuth = headers.has('authorization');
        const token = await this.options.getAccessToken?.();
        if (token && !callerSuppliedAuth) {
            headers.set('authorization', `Bearer ${token}`);
        }
        const hasBody = options.body !== undefined;
        if (hasBody && !headers.has('content-type')) {
            headers.set('content-type', 'application/json');
        }
        const send = async () => this.fetchImpl(url, {
            method,
            headers,
            credentials: 'include',
            ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
            ...(options.signal ? { signal: options.signal } : {}),
        });
        let response = await send();
        // Only when this request actually carried a token. A 401 from `/auth/login`
        // means the password was wrong, not that a session expired — asking to
        // refresh there rotates a refresh token for nothing, and on a shared
        // machine could revoke a chain belonging to whoever was signed in before.
        const sentCredentials = headers.has('authorization');
        // And only when the token was the session's. A 401 on a request carrying a
        // token the caller supplied says that token is no good — not that the
        // session needs refreshing. Recovering here would rotate a refresh token
        // this request never used and then retry with the session's token instead
        // of the caller's, quietly sending the request as somebody else.
        const usedTheSession = sentCredentials && !callerSuppliedAuth;
        if (response.status === 401 && usedTheSession && !options.isRetry) {
            const recovered = (await this.options.onUnauthorized?.()) ?? false;
            if (recovered) {
                // Rebuilt, because the token it carried is the one that just expired.
                // Removed rather than left stale if the caller somehow has none: a
                // second attempt with the same dead token is a wasted round trip.
                const token = await this.options.getAccessToken?.();
                if (token === undefined)
                    headers.delete('authorization');
                else
                    headers.set('authorization', `Bearer ${token}`);
                response = await send();
            }
        }
        if (!response.ok) {
            throw new ApiError(await this.readProblem(response), response);
        }
        if (response.status === 204 || response.headers.get('content-length') === '0') {
            return undefined;
        }
        return (await response.json());
    }
    buildUrl(path, query) {
        const url = `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
        if (!query)
            return url;
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(query)) {
            if (value !== undefined && value !== null)
                params.append(key, String(value));
        }
        const serialised = params.toString();
        return serialised ? `${url}?${serialised}` : url;
    }
    /** Falls back to a synthetic problem when a proxy returns a non-JSON error. */
    async readProblem(response) {
        try {
            const payload = (await response.json());
            if (typeof payload?.code === 'string' && typeof payload?.status === 'number') {
                return payload;
            }
        }
        catch {
            // Body was empty or not JSON — handled below.
        }
        return {
            type: 'urn:shift:error:unexpected-response',
            title: response.statusText || 'Unexpected response',
            status: response.status,
            code: 'UNEXPECTED_RESPONSE',
            detail: `The API returned ${response.status} without a problem document.`,
        };
    }
}
//# sourceMappingURL=http-client.js.map